import { useEffect, useState } from "react";
import { AlertCircle, ArrowRight, BellRing, CalendarDays, Clock, CreditCard, IndianRupee, Mail, MessageCircle, Ticket, UserPlus, Users } from "lucide-react";
import { api, formatDate, money } from "@/lib/api";

const Metric = ({ icon: Icon, label, value, hint, onClick, testId, tone }) => (
  <button type="button" className={`metric ${tone || ""}`} onClick={onClick} data-testid={testId}>
    <Icon size={20} />
    <strong>{value}</strong>
    <span>{label}</span>
    {hint && <small>{hint}</small>}
  </button>
);

export const AdminOverview = ({ go, role, onReview, refreshKey = 0 }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [remind, setRemind] = useState("");
  useEffect(() => { api("/admin/overview").then(setD).catch(e => setErr(e.message)); }, [refreshKey]);
  const runReminders = async () => {
    setRemind("Sending…");
    try { const r = await api("/admin/renewal-reminders/run", { method: "POST" }); setRemind(`Reminders sent to ${r.sent} of ${r.checked} expiring member(s).`); }
    catch (e) { setRemind(e.message); }
  };
  if (err) return <div className="error-message" data-testid="overview-error">{err}</div>;
  if (!d) return <div className="empty-state" data-testid="overview-loading">Loading overview…</div>;
  const money_ = v => money(v, "INR");
  const canFinance = ["super_admin", "association_admin"].includes(role);
  return (
    <div className="overview" data-testid="admin-overview">
      <div className="metric-grid">
        <Metric icon={Users} label="Active members" value={d.members} onClick={() => go("members")} testId="metric-members" />
        <Metric icon={UserPlus} label="Pending approvals" value={d.pending_members} hint="new member accounts" onClick={() => go("users")} testId="metric-pending" tone={d.pending_members ? "warn" : ""} />
        {canFinance && <Metric icon={CreditCard} label="Payments to verify" value={d.payments_pending} hint={`${d.payments_awaiting} awaiting payment`} onClick={() => go("payments")} testId="metric-payments" tone={d.payments_pending ? "warn" : ""} />}
        {canFinance && <Metric icon={IndianRupee} label="Fees collected" value={money_(d.revenue)} hint={`${d.paid_count} verified payments`} onClick={() => go("payments")} testId="metric-revenue" tone="gold" />}
        <Metric icon={CalendarDays} label="Upcoming events" value={d.upcoming_events} hint={`${d.registrations} registrations`} onClick={() => go("events")} testId="metric-events" />
        <Metric icon={Ticket} label="Registrations" value={d.registrations} onClick={() => go("registrations")} testId="metric-registrations" />
        {canFinance && <Metric icon={Mail} label="Unread messages" value={d.unread_messages} onClick={() => go("contacts")} testId="metric-messages" />}
        {canFinance && <Metric icon={BellRing} label="Expiring in 30 days" value={d.expiring_soon} hint="renewal reminders run daily 9:30 IST" onClick={() => go("users")} testId="metric-expiring" tone={d.expiring_soon ? "warn" : ""} />}
      </div>
      {canFinance && (
        <div className="hint" style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
          <BellRing size={16} /> Renewal reminders email annual members 30 days before expiry (daily, 9:30 IST).
          <button type="button" className="link" onClick={runReminders} data-testid="run-reminders-button">Send reminders now</button>
          {remind && <span data-testid="reminders-status">{remind}</span>}
        </div>
      )}
      {d.pending_approvals?.length > 0 && (
        <section className="approval-queue" data-testid="approval-queue">
          <div className="approval-queue-head">
            <div><span className="eyebrow">Needs your decision</span><h3><UserPlus size={18} /> {d.pending_approvals.length} applicant{d.pending_approvals.length === 1 ? "" : "s"} waiting for approval</h3></div>
            <small className="hint" style={{ padding: 0 }}>Review each one within 24 hours of applying</small>
          </div>
          <div className="approval-list">
            {d.pending_approvals.map(a => (
              <div className="list-row" key={a.ref} data-testid={`approval-row-${a.ref}`}>
                <div><b>{a.name || a.email}</b><small>{a.email}{a.phone ? ` · ${a.phone}` : ""} · applied {formatDate(a.applied_at)}</small></div>
                <span className={`pill sla ${a.overdue ? "danger" : a.hours_left < 6 ? "warn" : ""}`} data-testid={`approval-sla-${a.ref}`}><Clock size={11} /> {a.overdue ? `Overdue by ${Math.round(a.hours_elapsed - 24)}h` : `${Math.max(1, Math.round(a.hours_left))}h left`}</span>
                <button type="button" className="button button-gold" style={{ padding: "9px 16px", fontSize: 11 }} onClick={() => onReview(a.ref)} data-testid={`approval-review-${a.ref}`}>Review & approve <ArrowRight size={13} /></button>
              </div>
            ))}
          </div>
        </section>
      )}
      {d.reminder_events?.length > 0 && (
        <div className="hint reminder-due" data-testid="whatsapp-due">
          <MessageCircle size={16} /> <b>WhatsApp reminders due:</b>
          {d.reminder_events.map(ev => (
            <button type="button" className="link" key={ev.id} onClick={() => go("checkin", ev.id)} data-testid={`whatsapp-due-${ev.id}`}>
              {ev.title} · {ev.is_tomorrow ? "tomorrow" : "today"} · {ev.pending} of {ev.registrants} registrant(s) to remind <ArrowRight size={12} />
            </button>
          ))}
        </div>
      )}
      <div className="content-strip" data-testid="content-stats">
        {Object.entries(d.content).map(([k, v]) => (
          <button type="button" key={k} onClick={() => go(k === "albums" ? "albums" : k)} data-testid={`content-stat-${k}`}><strong>{v}</strong><span>{k}</span></button>
        ))}
      </div>
      <div className="overview-grid">
        {canFinance && (
          <section className="account-card" data-testid="recent-payments">
            <h3><CreditCard size={16} /> Recent payments</h3>
            {d.recent_payments.length ? d.recent_payments.map(p => (
              <div className="list-row" key={p.id}><div><b>{p.name}</b><small>{p.plan_label}{p.reference ? ` · Ref ${p.reference}` : ""}</small></div><strong>{money_(p.amount)}</strong><span className={`pill ${p.status}`}>{p.status.replace("_", " ")}</span></div>
            )) : <div className="hint">No payments yet.</div>}
          </section>
        )}
        <section className="account-card" data-testid="recent-registrations">
          <h3><Ticket size={16} /> Latest registrations</h3>
          {d.recent_registrations.length ? d.recent_registrations.map(r => (
            <div className="list-row" key={r.id}><div><b>{r.name}</b><small>{r.event_title || r.event_id} · {r.email}</small></div><span className="pill">{formatDate(r.created_at)}</span></div>
          )) : <div className="hint">No registrations yet.</div>}
        </section>
        <section className="account-card" data-testid="recent-members">
          <h3><Users size={16} /> New member accounts</h3>
          {d.recent_members.length ? d.recent_members.map(m => (
            <div className="list-row" key={m.user_id}><div><b>{m.name}</b><small>{m.email}</small></div><span className={`pill ${m.status}`}>{m.status}</span></div>
          )) : <div className="hint">No member accounts yet.</div>}
        </section>
      </div>
      {!!(d.pending_members || d.payments_pending) && canFinance && (
        <div className="hint" style={{ display: "flex", gap: 10, alignItems: "center" }} data-testid="overview-todo"><AlertCircle size={16} /> You have {d.payments_pending} payment(s) to verify and {d.pending_members} member account(s) awaiting activation.</div>
      )}
    </div>
  );
};
